/**
 * Create Journey Dialog
 *
 * Dialog for parents to create a new journey for their child.
 * Two modes:
 *  - "From a template": pick a ready-made template (filtered to the child's age).
 *  - "Build your own": give the journey a title and add custom steps.
 */

import { useMemo, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useJourneyTemplates, useJourneyTemplate, useStartJourneyFromTemplate } from '@/hooks/queries/useJourneyTemplates';
import { useCreateCustomJourney } from '@/hooks/queries/useJourneys';
import { useChildProfiles } from '@/hooks/queries/useChildProfiles';
import { useAuth } from '@/contexts/AuthContext';
import { Loader2, CheckCircle2, Plus, X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface CreateJourneyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  preselectedChildId?: string;
  onSuccess?: () => void;
}

interface CustomStep {
  id: string;
  title: string;
}

export function CreateJourneyDialog({
  open,
  onOpenChange,
  preselectedChildId,
  onSuccess,
}: CreateJourneyDialogProps) {
  const { user } = useAuth();
  const { data: templates = [], isLoading: templatesLoading } = useJourneyTemplates();
  const { data: children = [] } = useChildProfiles(user?.id);
  const startJourney = useStartJourneyFromTemplate();
  const createCustom = useCreateCustomJourney();

  const [mode, setMode] = useState<'template' | 'custom'>('template');
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [selectedChildId, setSelectedChildId] = useState<string>(preselectedChildId || '');

  // Monotonic counter for stable, collision-free custom-step keys.
  const stepIdCounter = useRef(0);
  const nextStepId = () => `step-${stepIdCounter.current++}`;

  // Custom journey state
  const [customTitle, setCustomTitle] = useState('');
  const [customSteps, setCustomSteps] = useState<CustomStep[]>([{ id: nextStepId(), title: '' }]);

  // Fetch selected template details (with steps) for preview
  const { data: selectedTemplateDetail } = useJourneyTemplate(selectedTemplateId ?? undefined);

  const selectedChild = children.find((c) => c.id === selectedChildId);

  // Only show templates appropriate for the selected child's age, so parents
  // don't pick one that the server would reject at start time.
  const eligibleTemplates = useMemo(() => {
    if (!selectedChild) return templates;
    return templates.filter(
      (t) => selectedChild.age >= t.age_range_min && selectedChild.age <= t.age_range_max
    );
  }, [templates, selectedChild]);

  const resetForm = () => {
    setMode('template');
    setSelectedTemplateId(null);
    setSelectedChildId(preselectedChildId || '');
    setCustomTitle('');
    setCustomSteps([{ id: nextStepId(), title: '' }]);
  };

  const handleClose = (isOpen: boolean) => {
    if (!isOpen) {
      resetForm();
    }
    onOpenChange(isOpen);
  };

  const handleCreateFromTemplate = async () => {
    if (!selectedTemplateId || !selectedChildId) return;

    try {
      await startJourney.mutateAsync({
        templateId: selectedTemplateId,
        childId: selectedChildId,
      });
      onSuccess?.();
      handleClose(false);
    } catch {
      // Error handled by mutation
    }
  };

  const handleCreateCustom = async () => {
    const title = customTitle.trim();
    const steps = customSteps
      .map((s) => ({ title: s.title.trim() }))
      .filter((s) => s.title.length > 0);

    if (!selectedChildId || !title || steps.length === 0) return;

    try {
      await createCustom.mutateAsync({
        child_profile_id: selectedChildId,
        title,
        steps,
      });
      onSuccess?.();
      handleClose(false);
    } catch {
      // Error handled by mutation
    }
  };

  const addStep = () =>
    setCustomSteps((prev) => [...prev, { id: nextStepId(), title: '' }]);

  const removeStep = (id: string) =>
    setCustomSteps((prev) => (prev.length > 1 ? prev.filter((s) => s.id !== id) : prev));

  const updateStep = (id: string, title: string) =>
    setCustomSteps((prev) => prev.map((s) => (s.id === id ? { ...s, title } : s)));

  const hasValidCustomSteps = customSteps.some((s) => s.title.trim().length > 0);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Create New Journey</DialogTitle>
          <DialogDescription>
            Start from a ready-made template or build your own for your child.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex flex-col gap-4">
          {/* Child selector (shared by both modes) */}
          <div className="space-y-2">
            <Label htmlFor="child">Assign to Child</Label>
            <Select value={selectedChildId} onValueChange={setSelectedChildId}>
              <SelectTrigger>
                <SelectValue placeholder="Select a child" />
              </SelectTrigger>
              <SelectContent>
                {children.map((child) => (
                  <SelectItem key={child.id} value={child.id}>
                    {child.name} (age {child.age})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Tabs value={mode} onValueChange={(v) => setMode(v as 'template' | 'custom')} className="flex-1 flex flex-col overflow-hidden">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="template">From a template</TabsTrigger>
              <TabsTrigger value="custom">Build your own</TabsTrigger>
            </TabsList>

            {/* ── Template mode ── */}
            <TabsContent value="template" className="flex-1 overflow-hidden space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Select Journey Template</Label>
                <ScrollArea className="h-64 rounded-md border p-3">
                  {templatesLoading ? (
                    <div className="flex items-center justify-center h-full">
                      <Loader2 className="h-6 w-6 animate-spin" />
                    </div>
                  ) : eligibleTemplates.length === 0 ? (
                    <div className="text-center text-muted-foreground py-8 space-y-2">
                      <p>
                        {selectedChild
                          ? `No templates for age ${selectedChild.age} yet.`
                          : 'No templates available'}
                      </p>
                      {selectedChild && (
                        <Button variant="outline" size="sm" onClick={() => setMode('custom')}>
                          Build your own instead
                        </Button>
                      )}
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 gap-2">
                      {eligibleTemplates.map((template) => (
                        <Card
                          key={template.id}
                          className={cn(
                            'cursor-pointer transition-all hover:shadow-warm border-2 border-transparent',
                            selectedTemplateId === template.id && 'border-primary bg-primary/5'
                          )}
                          onClick={() => setSelectedTemplateId(template.id)}
                        >
                          <CardHeader className="py-3">
                            <div className="flex items-center justify-between">
                              <CardTitle className="text-body-sm font-medium">
                                {template.title}
                              </CardTitle>
                              {selectedTemplateId === template.id && (
                                <CheckCircle2 className="h-4 w-4 text-primary" />
                              )}
                            </div>
                          </CardHeader>
                          <CardContent className="py-2">
                            <p className="text-caption text-muted-foreground line-clamp-2">
                              {template.description}
                            </p>
                            <div className="flex items-center gap-2 mt-2">
                              <Badge variant="secondary" className="text-caption">
                                {template.category}
                              </Badge>
                              <span className="text-caption text-muted-foreground">
                                {template.duration_days} days
                              </span>
                              <span className="text-caption text-muted-foreground">
                                Ages {template.age_range_min}-{template.age_range_max}
                              </span>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </div>

              {/* Preview selected template */}
              {selectedTemplateDetail && (
                <div className="rounded-lg border p-4 bg-muted/50">
                  <h4 className="font-medium text-body-sm mb-2">Journey Preview</h4>
                  <p className="text-caption text-muted-foreground mb-2">
                    {selectedTemplateDetail.description}
                  </p>
                  {selectedTemplateDetail.steps && selectedTemplateDetail.steps.length > 0 && (
                    <div className="space-y-1">
                      <p className="text-caption font-medium">{selectedTemplateDetail.steps.length} Steps:</p>
                      <ul className="text-caption text-muted-foreground space-y-1">
                        {selectedTemplateDetail.steps.slice(0, 3).map((step, i) => (
                          <li key={step.id || i}>• {step.title}</li>
                        ))}
                        {selectedTemplateDetail.steps.length > 3 && (
                          <li>• ... and {selectedTemplateDetail.steps.length - 3} more</li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </TabsContent>

            {/* ── Custom mode ── */}
            <TabsContent value="custom" className="flex-1 overflow-hidden space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="custom-title">Journey Title</Label>
                <Input
                  id="custom-title"
                  value={customTitle}
                  onChange={(e) => setCustomTitle(e.target.value)}
                  placeholder="e.g. Weekend Reading Challenge"
                />
              </div>

              <div className="space-y-2">
                <Label>Steps / Tasks</Label>
                <ScrollArea className="h-56 rounded-md border p-3">
                  <div className="space-y-2">
                    {customSteps.map((step, index) => (
                      <div key={step.id} className="flex items-center gap-2">
                        <span className="text-caption text-muted-foreground w-5 shrink-0">
                          {index + 1}.
                        </span>
                        <Input
                          value={step.title}
                          onChange={(e) => updateStep(step.id, e.target.value)}
                          placeholder="Describe the task"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && step.title.trim() && index === customSteps.length - 1) {
                              addStep();
                            }
                          }}
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-muted-foreground hover:text-destructive shrink-0"
                          onClick={() => removeStep(step.id)}
                          disabled={customSteps.length === 1}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
                <Button type="button" variant="outline" size="sm" onClick={addStep}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add step
                </Button>
              </div>
            </TabsContent>
          </Tabs>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleClose(false)}>
            Cancel
          </Button>
          {mode === 'template' ? (
            <Button
              onClick={handleCreateFromTemplate}
              disabled={!selectedTemplateId || !selectedChildId || startJourney.isPending}
            >
              {startJourney.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Create Journey
            </Button>
          ) : (
            <Button
              onClick={handleCreateCustom}
              disabled={
                !selectedChildId ||
                !customTitle.trim() ||
                !hasValidCustomSteps ||
                createCustom.isPending
              }
            >
              {createCustom.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Create Journey
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
